import { create } from 'zustand'

// DirectorJobStatusOptions (rolla-db prisma/schema.prisma): queued | processing | rendering |
// uploading | completed | failed | cancelled. Terminal states are 'completed', 'failed' and
// 'cancelled' — polling stops on any of them (see useRenderJobPolling).
export type RenderJobStatus =
  | 'queued'
  | 'processing'
  | 'rendering'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'cancelled'

export interface RenderJob {
  id: string
  projectName: string
  status: RenderJobStatus
  percent: number
  stage: string | null
  error: string | null
  // 'export' = a Premiere/FCP7 bundle (.zip) job, so the widget can label it apart from a video render.
  // Defaults to 'render' when omitted.
  kind?: 'render' | 'export'
  // ISO timestamp the job was created (shown as a relative "created …" hint on each row). Optional so
  // older callers / jobs without it still work.
  createdAt?: string
  // Download URL of the finished result (set on completion). For an export it's the .zip the widget
  // offers via "Download"; for a render the mp4 is viewed in Edited Content instead.
  downloadUrl?: string
}

interface RenderJobsState {
  jobs: RenderJob[]
  addJob: (job: RenderJob) => void
  updateJob: (id: string, patch: Partial<Omit<RenderJob, 'id'>>) => void
  removeJob: (id: string) => void
}

export const useRenderJobsStore = create<RenderJobsState>((set) => ({
  jobs: [],
  addJob: (job) => set((state) => ({ jobs: [...state.jobs, job] })),
  updateJob: (id, patch) =>
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...patch } : j)),
    })),
  removeJob: (id) => set((state) => ({ jobs: state.jobs.filter((j) => j.id !== id) })),
}))
